import { CurrencyPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import * as XLSX from 'exceljs/dist/exceljs.min.js';
import { MessageService } from 'primeng/api';
import { forkJoin } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { AmountTypeEnum, BLCTypeEnum } from '../../../models/enums';
import { CommonFunctions } from '../../../functions';
import {
    AmountType, Bank, BankAmountType, BankWithAmountTypes, BankWithFinancialGuarantee, Bond,
    Company, CostToCompleteByCompanyReport, CostToCompleteByCompanyReportParam, Document, ReportColumn
} from '../../../models';
import {
    AmountTypeService, AuthorizationService, BankAmountTypeService, BankService, BondService,
    CompanyService, DocumentService, ExcelService, ReportService
} from '../../../services';

@Component({
    selector: 'app-cost-to-complete-by-company',
    templateUrl: './cost-to-complete-by-company.component.html',
    styleUrls: ['./cost-to-complete-by-company.component.css'],
    providers: [MessageService]
})
export class CostToCompleteByCompanyComponent implements OnInit {
    amountTypeList: AmountType[] = [];
    availableCommitment: number = null;
    bankData: BankWithFinancialGuarantee;
    banksForDialog: Bank[] = [];
    bondData: Bond;
    cols: ReportColumn[];
    companyList: Company[] = [];
    companyListSource: Company[] = [];
    companyListTarget: Company[] = [];
    displayEditDialog: boolean = false;
    displayBankEditDialog: boolean = false;
    documents: Document[];
    exportHeader: string[] = [];
    filteredCount: any[] = [];
    isAmountTypeFinancialGuarantee: boolean;
    isLoading: boolean = false;
    isStatusCanceled: boolean;
    isValidParams: boolean = true;
    maxAmount: number = 999999999999.99; // 999 billion instead of 922337203685477.58 = Max value for SQL DB Type Money
    reportGridList: any[] = [];
    reportExcelList: any[] = [];
    reportParameter: CostToCompleteByCompanyReportParam = new CostToCompleteByCompanyReportParam();
    reportTitle: string = null;
    results: CostToCompleteByCompanyReport[] = [];
    selectedBank: BankWithAmountTypes;
    selectedBankAmountTypes: string[] = [];
    showResults: boolean = false;
    singleBondLimit: string = null;

    constructor(private amountTypeService: AmountTypeService,
        private authorizationService: AuthorizationService,
        private bankAmountTypeService: BankAmountTypeService,
        private bankService: BankService,
        private bondService: BondService,
        private commonFunctions: CommonFunctions,
        private companyService: CompanyService,
        private currencyPipe: CurrencyPipe,
        private documentService: DocumentService,
        private excelService: ExcelService,
        private messageService: MessageService,
        private reportService: ReportService) { }

    ngOnInit(): void {
        this.authorizationService.checkBlcUserAccess(false);

        this.reportParameter = new CostToCompleteByCompanyReportParam();

        this.amountTypeService.getAmountTypes()
            .subscribe((result: AmountType[]) => {
                this.amountTypeList = result;
                this.amountTypeList.sort((a, b) => a.name.localeCompare(b.name));
            }, () => {
                this.messageService.add({
                    severity: 'error', summary: 'Unexpected Error Occurred',
                    detail: 'An error occurred getting Amount Types.'
                });
            });

        this.companyService.getCompanies()
            .subscribe((companies) => {
                this.companyList = companies;
                this.companyList = this.commonFunctions.sortAndFormatCompanies(this.companyList);
                // Cloning this because the companyList gets updated with the remaining available companies once a selection has been made
                this.companyListSource = JSON.parse(JSON.stringify(this.companyList));
            }, error => {
                this.messageService.add({
                    severity: 'error', summary: 'Unexpected Error Occurred',
                    detail: 'An error occurred getting Companies.'
                });
            });

        this.isValidParams = this.validateReportParams(this.companyListTarget, this.reportParameter).length < 1 ? true : false;
    }

    exportExcel(): void {
        // Set export file name
        const exportFileName = 'COSTTOCOMPLETEBYCOMPANY';
        const workbook = new XLSX.Workbook();
        const worksheet = workbook.addWorksheet('Report Results');

        // Add title row
        const titleRow = worksheet.addRow([this.reportTitle]);
        worksheet.mergeCells('A1:K1');
        worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };

        // Set font, size and style in title row
        titleRow.font = { name: 'Calibri', family: 4, size: 16, underline: 'double', bold: true };

        // Blank Row
        worksheet.addRow([]);

        // Set column widths to fix header row
        worksheet.columns.forEach((column) => {
            column.width = 15;
        });

        // Add Header Row
        const headerRow = worksheet.addRow(this.exportHeader);

        // Wrap and align the header text
        headerRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };

        // Set header row height
        headerRow.height = 60;

        // Fill and Border Headers
        headerRow.eachCell((cell) => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'D3D3D3' },
                bgColor: { argb: '' }
            };
            cell.border = { top: { style: 'none' }, left: { style: 'none' }, bottom: { style: 'none' }, right: { style: 'none' } };
            cell.font = { weight: 'bold' };
        });

        let isSubHeaderRow = false;

        // Add Data
        this.reportExcelList.forEach(dataRow => {
            const dataStrings: any[] = [];

            // Convert each prop into a string array
            Object.values(dataRow).forEach((val: any, index: number) => {
                switch (index) {
                    case 9: //Estimated Release Date
                        if (val?.toString()) {
                            dataStrings.push(new Date(val));
                        }
                        else {
                            dataStrings.push(val);
                        }
                        break;
                    default: //Everything else
                        dataStrings.push(val);
                }
            });

            // Add the data row
            const row = worksheet.addRow(dataStrings);
            row.eachCell({ includeEmpty: true }, (cell => {
                cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            }));

            // Check if it is a eoy total row
            if (row.getCell(1).value !== null) {
                if (row.getCell(1).value.toString().includes('Total') || row.getCell(1).value.toString().includes('matches')) {
                    isSubHeaderRow = true;
                }
            }
            if (row.getCell(2).value !== null) {
                if (row.getCell(2).value.toString().includes('Total') || row.getCell(1).value.toString().includes('matches')) {
                    isSubHeaderRow = true;
                }
            }

            // Format the cells
            row.eachCell((cell) => {
                // If eoy total row, set to bold
                if (isSubHeaderRow) {
                    cell.font = { bold: true };
                }

                // If negative value, set font to red
                if (typeof cell.value === 'string' && cell.value.toString().includes('<') && cell.value.toString().includes('>')) {
                    cell.font = { color: { argb: 'FF0000' } };
                    cell.value = cell.value.replace('-', '');
                }
            });

            if (isSubHeaderRow) {
                isSubHeaderRow = false;
            }

        });

        this.excelService.exportAsExcelFile(workbook, exportFileName);
    }

    onBankModalEditDialogClose(): void {
        this.displayBankEditDialog = false;
        this.selectedBankAmountTypes = [];
        this.selectedBank = null;
    }

    onBondModalEditDialogClose(): void {
        this.displayEditDialog = false;
    }

    onGettingBankToView(bankId: number): void {
        try {
            this.bankService.getBankWithCommitment(bankId).pipe(
                switchMap((aresult: BankWithAmountTypes) => {
                    this.selectedBank = aresult;

                    //  get the bank amount types from selected bank !
                    return this.bankAmountTypeService.getBankAmountTypeByBank(this.selectedBank.id);
                })
            ).subscribe((bresult: BankAmountType[]) => {
                this.selectedBank.amountTypes = [];
                //  loop through the bank amoutn types result from selected bank !
                bresult.forEach(res => {
                    this.selectedBankAmountTypes.push(res.amountTypeId.toString());
                    this.selectedBank.amountTypes.push(res.amountTypeId);
                });
                //  display the edit bank dialog !
                this.displayBankEditDialog = true;
            }, () => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Bank Amount Types Not Loaded',
                    detail: `Please be advised, unable to get Bank Amount Types for ${this.selectedBank.name}.`
                });
                this.selectedBankAmountTypes = null;
                this.displayBankEditDialog = true;
            });
        } catch (error) {
            this.messageService.add({
                severity: 'error',
                summary: 'Unexpected Error Occurred',
                detail: 'An error occurred during multiple process after getting the bank or surety record.'
            });
        }
    }

    onGettingBondToView(bondId: number): void {
        try {
            if (bondId === null || bondId === undefined) {
                throw new Error(`Invalid BondId`);
            }

            const selectedBond = this.filteredCount.find(bond => bond.bondId == bondId);

            forkJoin(
                this.bondService.getBondById(selectedBond.bondId),
                this.bankService.getBanks(true),
                this.documentService.getDocumentsByBlcId(selectedBond.bondId),
                this.bankService.getAvailableCommitment(selectedBond.bankId, true),
                this.bankService.getAvailableCommitment(selectedBond.bankId, false)
            )
                .subscribe(([bond, banks, documents, fgAvailableCommitment, availableCommitment]) => {
                    if (bond === null || bond === undefined || bond?.id < 1) {
                        throw new Error(`Bond data for ${bondId} not found.`);
                    }

                    this.bondData = bond;
                    this.bondData.bondType = bond.isLetterOfCredit ? BLCTypeEnum.LetterOfCredit : BLCTypeEnum.Bond;
                    this.bondData.dateExpired = this.bondData.currentDateExpired;

                    const getBondStatusResult = this.commonFunctions.getBondStatus(this.bondData);
                    this.bondData = getBondStatusResult.modifiedBond;
                    this.isStatusCanceled = getBondStatusResult.isStatusCanceled;

                    if (this.bondData.amountTypeId === AmountTypeEnum.FinancialGuarantee) {
                        this.isAmountTypeFinancialGuarantee = true;
                        this.bankData = fgAvailableCommitment;
                        this.availableCommitment = this.bankData.financialGuaranteeAvailableCommitment;
                    } else {
                        this.isAmountTypeFinancialGuarantee = false;
                        this.bankData = availableCommitment;
                        this.availableCommitment = this.bankData.availableCommitment;
                    }

                    this.banksForDialog = banks;
                    this.banksForDialog.sort((a, b) => a.name.localeCompare(b.name));
                    const result2 = this.commonFunctions.setSingleBondLimit(this.banksForDialog, this.bondData);
                    this.singleBondLimit = result2.sbl;

                    this.documents = documents as Document[];
                    this.displayEditDialog = true;
                }, () => {
                    this.messageService.add({
                        severity: 'error', summary: 'Unexpected Error Occurred',
                        detail: 'An error occurred getting the BLC.'
                    });
                })
        } catch (error) {
            this.messageService.add({
                severity: 'error',
                summary: 'Unexpected Error Occurred',
                detail: 'An error occurred during multiple process after getting the BLC record.'
            });
        }
    }

    onInputChange(eventValue: any, inputName: any) {
        this.showResults = false;
        this.reportGridList = [];

        if ((inputName == 'fromAmount' || inputName == 'toAmount') && eventValue > this.maxAmount) {
            this.messageService.add({
                severity: 'error', summary: 'Invalid Value',
                detail: (inputName == 'fromAmount' ? 'From Amount' : 'To Amount') + ' must not exceed ' + this.currencyPipe.transform(this.maxAmount, 'USD', 'symbol', '1.2-2') + '.'
            });

            if (inputName == 'fromAmount') {
                this.reportParameter.fromAmount = 0;
            } else {
                this.reportParameter.toAmount = 0;
            }
        }
    }

    onLinkClickEvent(event: any): void {
        const resultArray = event.data.split('_');
        const type = resultArray[0];
        const id = resultArray[1];

        if (type === 'bankId') {
            this.onGettingBankToView(id);
        } else if (type === 'bondId') {
            this.onGettingBondToView(id);
        } else {
            this.messageService.add({
                severity: 'error',
                summary: 'Unexpected Error Occurred',
                detail: 'Invalid link type on selected record'
            });
        }
    }

    onMoveSourceToTarget(): void {
        this.showResults = false;
        this.reportGridList = [];
        this.companyListTarget.sort((src: Company, tgt: Company) => src.marketNumber.localeCompare(tgt.marketNumber));
        this.isValidParams = this.validateReportParams(this.companyListTarget, this.reportParameter).length < 1 ? true : false;
    }

    onMoveTargetToSource(): void {
        this.showResults = false;
        this.reportGridList = [];
        this.companyList.sort((src: Company, tgt: Company) => src.marketNumber.localeCompare(tgt.marketNumber));
        this.isValidParams = this.validateReportParams(this.companyListTarget, this.reportParameter).length < 1 ? true : false;
    }

    onResetSearch(): void {
        this.companyList.push(...this.companyListTarget);
        this.companyList.sort((src: Company, tgt: Company) => src.marketNumber.localeCompare(tgt.marketNumber));
        this.companyListTarget = [];

        this.reportGridList = [];
        this.reportExcelList = [];
        this.exportHeader = [];
        this.showResults = false;

        this.reportParameter = new CostToCompleteByCompanyReportParam();
        this.isValidParams = this.validateReportParams(this.companyListTarget, this.reportParameter).length < 1 ? true : false;
    }

    onSearch(): void {
        try {
            this.reportGridList = [];
            this.reportExcelList = [];
            this.exportHeader = [];

            const validatedParams = this.validateReportParams(this.companyListTarget, this.reportParameter);
            this.isValidParams = validatedParams.length < 1 ? true : false;
            for (const validatedItem of validatedParams) {
                if (validatedItem.isValid === false) {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Entry Validation Error Occurred',
                        detail: validatedItem.message
                    });
                    return;
                }
            }

            // Set search params and report results title
            this.reportTitle = `Cost to Complete by Company`;

            // Set result table columns
            this.cols = [
                { field: 'company', header: 'Company' },
                { field: 'community', header: 'Community' },
                { field: 'bank', header: 'Bank/Surety', link: 'bankId' },
                { field: 'bondAmount', header: 'Current Amount', dataType: 'currency' },
                { field: 'beneficiary', header: 'Beneficiary' },
                { field: 'bondType', header: 'Type' },
                { field: 'bondNumber', header: 'Bond#', link: 'bondId' },
                { field: 'costToComplete', header: 'Cost To Complete (%)', dataType: 'decimal' },
                { field: 'costToCompleteAmt', header: 'Cost To Complete ($)', dataType: 'currency' },
                { field: 'estimatedReleaseDate', header: 'Estimated Release Date', dataType: `date` },
                { field: 'comments', header: 'Comments', widthPercentage: 10 }
            ];

            // Set header row values for export
            for (const col of this.cols) {
                if (col.header) {
                    this.exportHeader.push(col.header);
                }
            }

            this.reportParameter.companyNumbers = [];
            for (const oitem of this.companyListTarget) {
                if ((oitem.marketNumber) || oitem.marketNumber != null) {
                    this.reportParameter.companyNumbers.push(oitem.marketNumber);
                }
            }

            this.reportService.getCostToCompleteByCompany(this.reportParameter)
                .subscribe((resultSet: CostToCompleteByCompanyReport[]) => {
                    for (var reportRow of resultSet) {
                        reportRow.communityNumberSortKey = this.commonFunctions.convertMarketOrCommunityToNumerics(reportRow.communityNumber);
                        reportRow.marketNumberSortKey = this.commonFunctions.convertMarketOrCommunityToNumerics(reportRow.marketNumber);
                    }

                    var ctcCurrentAmmountTotal = 0;
                    var ctcTotalPct = 0;
                    var ctcTotalAmt = 0;
                    const decimalFormat = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
                    const groupedBy = this.commonFunctions.groupBy(resultSet, `marketNumberSortKey`);

                    if (Object.keys(groupedBy).length > 0) {
                        for (const rpt in groupedBy) {
                            //  get total ctc matches!
                            const report = groupedBy[rpt];
                            const ctcMatches = report?.length;

                            this.reportGridList.push({
                                company: `${ctcMatches} matches`,
                                community: `Cost to Complete Report For ${report[0]?.company}`,
                                bank: null,
                                bondAmount: null,
                                beneficiary: null,
                                bondType: null,
                                bondNumber: null,
                                costToComplete: null,
                                costToCompleteAmt: null,
                                estimatedReleaseDate: null,
                                comments: null,
                                groupKey: 10000
                            });

                            this.reportExcelList.push({
                                company: `${ctcMatches} matches`,
                                community: `Cost to Complete Report For ${report[0]?.company}`,
                                bank: null,
                                bondAmount: null,
                                beneficiary: null,
                                bondType: null,
                                bondNumber: null,
                                costToComplete: null,
                                costToCompleteAmt: null,
                                estimatedReleaseDate: null,
                                comments: null,
                            });

                            const arrcomp = report.map(b => b.company);
                            const cmpy = new Set(arrcomp);
                            const uniqueCompanies = Array.from(cmpy);

                            var filtered: CostToCompleteByCompanyReport[] = [];
                            for (const theCompany of uniqueCompanies) {
                                var currentAmountTotal: number = 0;
                                var ctcPct: number = 0;
                                var ctcAmt: number = 0;
                                filtered = report.filter(cmpy => cmpy.company === theCompany);

                                for (const ctcRecord of filtered) {
                                    this.commonFunctions.trimTimeFromDatePropertiesInObject(ctcRecord);

                                    //  resolve filteredCount here for the report result components filteredCount input
                                    this.filteredCount.push(ctcRecord);
                                    currentAmountTotal += ctcRecord.bondAmount;
                                    ctcPct += ctcRecord.costToComplete;
                                    ctcAmt += ctcRecord.costToCompleteAmt;
                                    ctcCurrentAmmountTotal += ctcRecord.bondAmount;
                                    ctcTotalPct += ctcRecord.costToComplete;
                                    ctcTotalAmt += ctcRecord.costToCompleteAmt;

                                    this.reportGridList.push({
                                        bankId: ctcRecord.bankId,
                                        bondId: ctcRecord.bondId,
                                        company: ctcRecord.company,
                                        community: ctcRecord.community,
                                        bank: ctcRecord.bank,
                                        bondAmount: (ctcRecord.bondAmount) ? parseFloat(ctcRecord.bondAmount.toFixed(2)) : 0.00,
                                        beneficiary: ctcRecord.beneficiary,
                                        bondType: ctcRecord.bondType,
                                        bondNumber: ctcRecord.bondNumber,
                                        costToComplete: (ctcRecord.costToComplete) ? parseFloat(ctcRecord.costToComplete.toFixed(2)) : 0.00,
                                        costToCompleteAmt: (ctcRecord.costToCompleteAmt) ? parseFloat(ctcRecord.costToCompleteAmt.toFixed(2)) : 0.00,
                                        estimatedReleaseDate: ctcRecord.estimatedReleaseDate,
                                        comments: ctcRecord.comments,
                                    });

                                    const formAmount = (ctcRecord.costToCompleteAmt) ? parseFloat(ctcRecord.costToCompleteAmt.toFixed(2)).toLocaleString('en', decimalFormat) : `0.00`;
                                    const formBondAmount = (ctcRecord.bondAmount) ? parseFloat(ctcRecord.bondAmount.toFixed(2)).toLocaleString('en', decimalFormat) : `0.00`;
                                    const formCTC = (ctcRecord.costToComplete) ? parseFloat(ctcRecord.costToComplete.toFixed(2)).toLocaleString('en', decimalFormat) : `0.00`;

                                    this.reportExcelList.push({
                                        company: ctcRecord.company,
                                        community: ctcRecord.community,
                                        bank: ctcRecord.bank,
                                        bondAmount: ctcRecord.bondAmount < 0 ? `<$${formBondAmount}>` : `$${formBondAmount}`,
                                        beneficiary: ctcRecord.beneficiary,
                                        bondType: ctcRecord.bondType,
                                        bondNumber: ctcRecord.bondNumber,
                                        costToComplete: formCTC,
                                        costToCompleteAmt: +ctcRecord.costToCompleteAmt < 0 ? `<$${formAmount}>` : `$${formAmount}`,
                                        estimatedReleaseDate: ctcRecord.estimatedReleaseDate,
                                        comments: ctcRecord.comments,
                                    });
                                }

                                if (filtered.length != 0) {
                                    const formCurrentAmountTotal = (currentAmountTotal) ? parseFloat(currentAmountTotal.toFixed(2)).toLocaleString('en', decimalFormat) : `0.00`;
                                    const ctcAmtTotal = (ctcAmt) ? parseFloat(ctcAmt.toFixed(2)).toLocaleString('en', decimalFormat) : `0.00`;
                                    const ctcPercent = (Number(ctcAmt) / Number(currentAmountTotal)).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 2 });

                                    this.reportGridList.push({
                                        company: null,
                                        community: `Cost To Complete Total For ${theCompany}`,
                                        bank: null,
                                        bondAmount: currentAmountTotal,
                                        beneficiary: null,
                                        bondType: null,
                                        bondNumber: null,
                                        costToComplete: ctcPercent,
                                        costToCompleteAmt: ctcAmt,
                                        estimatedReleaseDate: null,
                                        comments: null,
                                        groupKey: 11000
                                    });

                                    this.reportExcelList.push({
                                        company: null,
                                        community: `Cost To Complete Total For ${theCompany}`,
                                        bank: null,
                                        bondAmount: +currentAmountTotal < 0 ? `<$${formCurrentAmountTotal}>` : `$${formCurrentAmountTotal}`,
                                        beneficiary: null,
                                        bondType: null,
                                        bondNumber: null,
                                        costToComplete: +ctcPct < 0 ? `<${ctcPercent}>` : `${ctcPercent}`,
                                        costToCompleteAmt: +ctcAmt < 0 ? `<$${ctcAmtTotal}>` : `$${ctcAmtTotal}`,
                                        estimatedReleaseDate: null,
                                        comments: null,
                                    });
                                } else {
                                    this.reportGridList.push({
                                        company: `No matches found.`,
                                        community: null,
                                        bank: null,
                                        bondAmount: null,
                                        beneficiary: null,
                                        bondType: null,
                                        bondNumber: null,
                                        costToComplete: null,
                                        costToCompleteAmt: null,
                                        estimatedReleaseDate: null,
                                        comments: null,
                                        groupKey: 12000
                                    });

                                    this.reportExcelList.push({
                                        company: `No matches found.`,
                                        community: null,
                                        bank: null,
                                        bondAmount: null,
                                        beneficiary: null,
                                        bondType: null,
                                        bondNumber: null,
                                        costToComplete: null,
                                        costToCompleteAmt: null,
                                        estimatedReleaseDate: null,
                                        comments: null,
                                    });
                                }
                            }
                        }
                    }
                    else {
                        for (const com of this.companyListTarget) {
                            this.reportGridList.push({
                                company: `0 matches`,
                                community: `Cost to Complete Report For ${com?.companyName}`,
                                bank: null,
                                bondAmount: null,
                                beneficiary: null,
                                bondType: null,
                                bondNumber: null,
                                costToComplete: null,
                                costToCompleteAmt: null,
                                estimatedReleaseDate: null,
                                comments: null,
                                groupKey: 10000
                            });

                            this.reportExcelList.push({
                                company: `0 matches`,
                                community: `Cost to Complete Report For ${com?.companyName}`,
                                bank: null,
                                bondAmount: null,
                                beneficiary: null,
                                bondType: null,
                                bondNumber: null,
                                costToComplete: null,
                                costToCompleteAmt: null,
                                estimatedReleaseDate: null,
                                comments: null,
                            });

                            this.reportGridList.push({
                                company: `No matches found.`,
                                community: null,
                                bank: null,
                                bondAmount: null,
                                beneficiary: null,
                                bondType: null,
                                bondNumber: null,
                                costToComplete: null,
                                costToCompleteAmt: null,
                                estimatedReleaseDate: null,
                                comments: null,
                                groupKey: 12000
                            });

                            this.reportExcelList.push({
                                company: `No matches found.`,
                                community: null,
                                bank: null,
                                bondAmount: null,
                                beneficiary: null,
                                bondType: null,
                                bondNumber: null,
                                costToComplete: null,
                                costToCompleteAmt: null,
                                estimatedReleaseDate: null,
                                comments: null,
                            });
                        }
                        this.showResults = true;
                    }
                    const formCtcCurrentTotal = (ctcCurrentAmmountTotal) ? parseFloat(ctcCurrentAmmountTotal.toFixed(2)).toLocaleString('en', decimalFormat) : `0.00`;
                    const formCtcAmtTotal = (ctcTotalAmt) ? parseFloat(ctcTotalAmt.toFixed(2)).toLocaleString('en', decimalFormat) : `0.00`;
                    const ctcTotalPercent = (Number(ctcTotalAmt) / Number(ctcCurrentAmmountTotal)).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 2 });

                    this.reportGridList.push({
                        company: `Cost To Complete Total`,
                        community: null,
                        bank: null,
                        bondAmount: ctcCurrentAmmountTotal,
                        beneficiary: null,
                        bondType: null,
                        bondNumber: null,
                        costToComplete: ctcTotalPercent,
                        costToCompleteAmt: ctcTotalAmt,
                        estimatedReleaseDate: null,
                        comments: null,
                        groupKey: 13000
                    });

                    this.reportExcelList.push({
                        company: `Cost To Complete Total`,
                        community: null,
                        bank: null,
                        bondAmount: +ctcCurrentAmmountTotal < 0 ? `<$${formCtcCurrentTotal}>` : `$${formCtcCurrentTotal}`,
                        beneficiary: null,
                        bondType: null,
                        bondNumber: null,
                        costToComplete: +ctcTotalPct < 0 ? `<${ctcTotalPercent}>` : `${ctcTotalPercent}`,
                        costToCompleteAmt: +ctcTotalAmt < 0 ? `<$${formCtcAmtTotal}>` : `$${formCtcAmtTotal}`,
                        estimatedReleaseDate: null,
                        comments: null,
                    });

                    this.showResults = true;
                });
        } catch (error) {
            this.messageService.add({
                severity: 'error',
                summary: 'Report Layout Error Occurred',
                detail: `There were error(s) occurred during report layout process.`
            });
        }
        finally {
            this.filteredCount = [];
        }
    }

    validateReportParams(cotList: Company[], param: CostToCompleteByCompanyReportParam): any[] {
        var retval: any[] = [];
        if (cotList.length < 1) {
            retval.push({
                isValid: false,
                message: `No company found in the Selected Company list box. Select a company.`
            });
        }

        return retval;
    }
}
